// navigationUtils.js - Navigation utility functions

import { checkAdminStatus } from './authUtils';

/**
 * The full set of individually-blockable feature-area pages, grouped the same way the nav is.
 * A Super Admin (or scoped Admin) can block any of these per-user (see UserManagement.js's
 * Feature Access section, PUT /api/v1/admin/users/{id}/feature-access). Keep this list in sync
 * with the backend's FeatureAccessFilter (D:\Chatbot\rag\...\config\FeatureAccessFilter.java) —
 * that's what actually enforces a block; this list just drives what the UI offers to toggle.
 * Three keys (LEDGER, CREDIT, SALES_PURCHASES) share a backend API surface with a sibling page
 * and can only be hidden in the UI, not independently blocked server-side — noted inline.
 */
export const FEATURE_GROUPS = [
  { label: 'Finance', items: [
    { key: 'FINANCE_DASHBOARD', label: 'Dashboard' },
    { key: 'LEDGER', label: 'Chart of Accounts', sharedBackendWith: 'FINANCE_DASHBOARD' },
    { key: 'INVOICES', label: 'Invoices' },
    { key: 'EXPENSES', label: 'Expenses' },
    { key: 'PAYMENTS', label: 'Payments' },
    { key: 'CREDIT', label: 'Credit & Outstanding', sharedBackendWith: 'CUSTOMERS/PURCHASES' },
    { key: 'REMINDERS', label: 'Reminders' },
    { key: 'EXCHANGE_RATES', label: 'Exchange Rates' },
    { key: 'REPORTS', label: 'Reports' },
  ]},
  { label: 'Trade', items: [
    { key: 'SALES_PURCHASES', label: 'Sales & Purchases', sharedBackendWith: 'INVOICES/PURCHASES' },
    { key: 'CUSTOMERS', label: 'Customers' },
    { key: 'GD', label: 'GD & Exports' },
    { key: 'COSTING', label: 'Shipment Costing' },
  ]},
  { label: 'Operations', items: [
    { key: 'BATCHES', label: 'Batches' },
    { key: 'GATE_PASSES', label: 'Gate Passes' },
    { key: 'PURCHASES', label: 'Purchases & Vendors' },
    { key: 'PRODUCTS', label: 'Product Catalog' },
  ]},
  { label: 'Admin', items: [
    { key: 'UPLOAD_DOCUMENT', label: 'Upload Document' },
    { key: 'KPI_EXTRACTION', label: 'KPI Extraction' },
    { key: 'KPI_DASHBOARD', label: 'KPI Dashboard' },
  ]},
];

const isBlocked = (userData, featureKey) => {
  const blocked = userData?.blockedFeatures;
  return Array.isArray(blocked) && blocked.includes(featureKey);
};

/**
 * Get navigation items based on user permissions
 * @param {Object} userData - User data
 * @returns {Array} - Array of navigation items
 */
export const getNavigationItems = (userData) => {
  return getGroupedNavigationItems(userData).flatMap(g => g.items);
};

export const getGroupedNavigationItems = (userData) => {
  const { isAdmin, isSuperAdmin, isAuthenticated } = checkAdminStatus(userData);

  // 3-role model: USER carries full day-to-day operational access on its own (batches, gate
  // passes, invoices, purchases, etc — the same access the old ACCOUNTANT/INVENTORY_MANAGER/
  // FARMER/RICE_USER roles used to gate individually, now folded into plain USER). So any
  // authenticated user sees these groups; only people-management is admin-gated below. Any
  // item can additionally be blocked per-user regardless of role — see FEATURE_GROUPS above.
  const hasFinanceAccess = isAuthenticated;
  const hasStockAccess   = isAuthenticated;
  const hasBatchAccess    = isAuthenticated;
  const hasAdminAccess   = isSuperAdmin || isAdmin;
  const allowed = (featureKey) => !isBlocked(userData, featureKey);

  const groups = [
    { label: "Chat", icon: "ChatIcon", single: true, items: [
      { label: "Chat", path: "/", icon: "ChatIcon" }
    ]},
  ];

  if (hasFinanceAccess) {
    const financeItems = [
      { label: "Dashboard",         path: "/finance",         icon: "MoneyIcon", featureKey: "FINANCE_DASHBOARD" },
      { label: "Chart of Accounts", path: "/ledger",          icon: "MoneyIcon", featureKey: "LEDGER" },
      { label: "Invoices",          path: "/invoices",        icon: "InvoiceIcon", featureKey: "INVOICES" },
      { label: "Expenses",          path: "/expenses",        icon: "ExpenseIcon", featureKey: "EXPENSES" },
      { label: "Payments",          path: "/payments",        icon: "PaymentsIcon", featureKey: "PAYMENTS" },
      { label: "Credit & Outstanding", path: "/credit",       icon: "CreditIcon", featureKey: "CREDIT" },
      { label: "Reminders",         path: "/reminders",       icon: "RemindersIcon", featureKey: "REMINDERS" },
      { label: "Exchange Rates",    path: "/exchange-rates",  icon: "ExchangeRateIcon", featureKey: "EXCHANGE_RATES" },
      { label: "Reports",          path: "/reports",         icon: "ReportsIcon", featureKey: "REPORTS" },
    ].filter(item => allowed(item.featureKey));
    if (financeItems.length > 0) groups.push({ label: "Finance", icon: "MoneyIcon", items: financeItems });

    const tradeItems = [
      { label: "Sales & Purchases", path: "/sales-purchases", icon: "SwapIcon", featureKey: "SALES_PURCHASES" },
      { label: "Customers",         path: "/customers",       icon: "PeopleIcon", featureKey: "CUSTOMERS" },
      { label: "GD & Exports",      path: "/gd",              icon: "GDIcon", featureKey: "GD" },
      { label: "Shipment Costing",     path: "/costing",         icon: "CostingIcon", featureKey: "COSTING" },
    ].filter(item => allowed(item.featureKey));
    if (tradeItems.length > 0) groups.push({ label: "Trade", icon: "SwapIcon", items: tradeItems });
  }

  const opsItems = [];
  if (hasBatchAccess && allowed("BATCHES"))     opsItems.push({ label: "Batches",           path: "/batches",      icon: "BatchIcon", featureKey: "BATCHES" });
  if (hasStockAccess && allowed("GATE_PASSES")) opsItems.push({ label: "Gate Passes",       path: "/gate-passes",  icon: "GatePassIcon", featureKey: "GATE_PASSES" });
  if (hasFinanceAccess && allowed("PURCHASES")) opsItems.push({ label: "Purchases & Vendors",path: "/purchases",   icon: "PurchaseIcon", featureKey: "PURCHASES" });
  if (hasFinanceAccess && allowed("PRODUCTS"))  opsItems.push({ label: "Product Catalog",   path: "/products",     icon: "ProductIcon", featureKey: "PRODUCTS" });
  // Wastage merged into Batch → Grading tab 2026-08-18 — no longer a standalone nav item.
  if (!hasFinanceAccess && hasStockAccess && allowed("PAYMENTS"))
    opsItems.push({ label: "Payments", path: "/payments", icon: "PaymentsIcon", featureKey: "PAYMENTS" });
  if (opsItems.length > 0) {
    groups.push({ label: "Operations", icon: "GatePassIcon", items: opsItems });
  }

  if (hasAdminAccess) {
    const adminItems = [
      { label: "User Management",   path: "/user-management",  icon: "PersonAddIcon" },
      { label: "Upload Document",   path: "/upload",           icon: "UploadIcon", featureKey: "UPLOAD_DOCUMENT" },
      { label: "KPI Extraction",    path: "/kpi-extraction",   icon: "AssessmentIcon", featureKey: "KPI_EXTRACTION" },
      { label: "KPI Dashboard",     path: "/kpi-dashboard",    icon: "AssessmentIcon", featureKey: "KPI_DASHBOARD" },
    ].filter(item => allowed(item.featureKey));
    if (isSuperAdmin) {
      adminItems.push(
        { label: "Create Department", path: "/create-department", icon: "DepartmentIcon" },
        { label: "Create Organization", path: "/create-org",      icon: "BusinessIcon" }
      );
    }
    groups.push({ label: "Admin", icon: "SettingsIcon", items: adminItems });
  }

  return groups;
};

/**
 * Get tab value based on current path
 * @param {string} pathname - Current pathname
 * @returns {number} - Tab index
 */
export const getTabValue = (pathname) => {
  switch (pathname) {
    case "/": return 0;
    case "/finance": return 1;
    case "/invoices": return 2;
    case "/expenses": return 3;
    case "/customers": return 4;
    case "/stock": return 5;
    case "/create-user": return 6;
    case "/create-department": return 7;
    case "/create-org": return 8;
    case "/upload": return 9;
    default: return 0;
  }
};

/**
 * Get status info based on current path
 * @param {string} pathname - Current pathname
 * @returns {Object} - Status object with label and color
 */
export const getStatusInfo = (pathname) => {
  switch (pathname) {
    case "/": return { label: "Active Chat", color: "success" };
    case "/finance": return { label: "Finance Dashboard", color: "success" };
    case "/ledger": return { label: "Chart of Accounts", color: "success" };
    case "/invoices": return { label: "Invoicing", color: "info" };
    case "/expenses": return { label: "Expenses", color: "warning" };
    case "/customers": return { label: "Customers", color: "info" };
    case "/stock": return { label: "Inventory", color: "info" };
    case "/gate-passes": return { label: "Gate Passes", color: "info" };
    case "/payments": return { label: "Payments & Cash", color: "success" };
    case "/gd": return { label: "GD & Exports", color: "info" };
    case "/reports": return { label: "Reports", color: "info" };
    case "/credit": return { label: "Credit & Outstanding", color: "warning" };
    case "/reminders": return { label: "Reminders & Alerts", color: "error" };
    case "/exchange-rates": return { label: "Exchange Rates", color: "info" };
    case "/costing": return { label: "Shipment Costing", color: "success" };
    case "/wastage": return { label: "Wastage Tracking", color: "warning" };
    case "/products": return { label: "Product Master", color: "info" };
    case "/purchases": return { label: "Purchases & Vendors", color: "info" };
    case "/batches": return { label: "Batch Management", color: "success" };
    case "/create-org": return { label: "Setup", color: "info" };
    case "/upload": return { label: "Upload", color: "warning" };
    case "/kpi-extraction": return { label: "KPI Extraction", color: "info" };
    case "/kpi-dashboard": return { label: "KPI Dashboard", color: "success" };
    case "/sales-purchases": return { label: "Sales & Purchases", color: "info" };
    case "/user-management": return { label: "User Management", color: "info" };
    case "/create-user": return { label: "Create User", color: "info" };
    case "/create-department": return { label: "Create Department", color: "info" };
    case "/tasks": return { label: "Tasks", color: "info" };
    default: return { label: "Ready", color: "default" };
  }
};

/**
 * Check if current path requires admin access
 * @param {string} pathname - Current pathname
 * @returns {boolean} - True if admin access required
 */
export const requiresAdminAccess = (pathname) => {
  const adminPaths = ['/create-user', '/upload', '/kpi-extraction', '/kpi-dashboard', '/finance', '/invoices', '/customers'];
  const superAdminPaths = ['/create-department', '/create-org'];

  return adminPaths.includes(pathname) || superAdminPaths.includes(pathname);
};

/**
 * Check if current path requires super admin access
 * @param {string} pathname - Current pathname
 * @returns {boolean} - True if super admin access required
 */
export const requiresSuperAdminAccess = (pathname) => {
  const superAdminPaths = ['/create-department', '/create-org'];
  return superAdminPaths.includes(pathname);
};

/**
 * Get page title based on current path
 * @param {string} pathname - Current pathname
 * @returns {string} - Page title
 */
export const getPageTitle = (pathname) => {
  switch (pathname) {
    case "/": return "Chat";
    case "/finance": return "Financial Dashboard";
    case "/invoices": return "Invoice Management";
    case "/expenses": return "Expense Management";
    case "/customers": return "Customer Management";
    case "/stock": return "Stock Management";
    case "/create-user": return "Create User";
    case "/create-department": return "Create Department";
    case "/create-org": return "Create Organization";
    case "/upload": return "Upload Document";
    case "/kpi-extraction": return "KPI Extraction";
    case "/kpi-dashboard": return "KPI Dashboard";
    default: return "AI Assistant";
  }
};

/**
 * Get breadcrumb items for current path
 * @param {string} pathname - Current pathname
 * @returns {Array} - Breadcrumb items
 */
export const getBreadcrumbs = (pathname) => {
  const breadcrumbs = [
    { label: "Home", path: "/" }
  ];

  switch (pathname) {
    case "/finance":
      breadcrumbs.push({ label: "Financial Dashboard", path: "/finance" });
      break;
    case "/invoices":
      breadcrumbs.push({ label: "Invoices", path: "/invoices" });
      break;
    case "/expenses":
      breadcrumbs.push({ label: "Expenses", path: "/expenses" });
      break;
    case "/customers":
      breadcrumbs.push({ label: "Customers", path: "/customers" });
      break;
    case "/stock":
      breadcrumbs.push({ label: "Stock", path: "/stock" });
      break;
    case "/create-user":
      breadcrumbs.push({ label: "Create User", path: "/create-user" });
      break;
    case "/create-department":
      breadcrumbs.push({ label: "Create Department", path: "/create-department" });
      break;
    case "/create-org":
      breadcrumbs.push({ label: "Create Organization", path: "/create-org" });
      break;
    case "/upload":
      breadcrumbs.push({ label: "Upload Document", path: "/upload" });
      break;
    case "/kpi-extraction":
      breadcrumbs.push({ label: "KPI Extraction", path: "/kpi-extraction" });
      break;
    case "/kpi-dashboard":
      breadcrumbs.push({ label: "KPI Dashboard", path: "/kpi-dashboard" });
      break;
    default:
      break;
  }

  return breadcrumbs;
};
